import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Row, Col, Typography, Table, Tag, Progress, Statistic, Card, Alert } from 'antd';
import {
  DatabaseOutlined,
  CloudUploadOutlined,
  CloudOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  HddOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { DashboardLayout } from '../components/layout';
import { StatCard, DataCard } from '../components/common';
import { useTenant } from '../context/TenantContext';
import {
  useAcronisTenants,
  useAcronisResources,
  useAcronisResourceUsage,
  useAcronisM365Backup,
  useAcronisIdFromCipp,
} from '../hooks';

const { Title, Text } = Typography;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

// Table chrome comes from the root ConfigProvider (theme spec section 2.4): no page-local .ant-* overrides.
const StyledTable = styled(Table)``;

const StorageCard = styled(Card)`
`;

const MappingAlert = styled(Alert)`
  margin-bottom: 24px;
`;

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return '--';
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Parse MCP response data from Acronis-MCP
 * Handles various response formats (string, content array, direct object)
 */
const parseMCPResponse = (data) => {
  if (!data) return null;

  // Handle string responses
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  // Handle direct data object
  if (data.data) {
    return data;
  }

  // Handle MCP content array format
  if (data.content?.[0]?.text) {
    try {
      return JSON.parse(data.content[0].text);
    } catch {
      return null;
    }
  }

  return data;
};

const AcronisPage = () => {
  const { selectedTenant } = useTenant();
  const cippTenantId = selectedTenant?.id;

  // Resolve CIPP tenant ID to Acronis tenant ID using customer mapping
  const {
    acronisId,
    isResolved: hasAcronisMapping,
    isLoading: isMappingLoading,
    customer,
  } = useAcronisIdFromCipp(cippTenantId, {
    enabled: !!cippTenantId,
  });

  // Fetch Acronis tenants list (for 'All Tenants' view)
  const { data: tenantsData, isLoading: tenantsLoading } = useAcronisTenants({
    enabled: !cippTenantId,
  });

  // Fetch Acronis resources using get_protected_resources (accurate counts)
  const { data: resourcesData, isLoading: resourcesLoading } = useAcronisResources(acronisId, {
    enabled: !!acronisId,
  });

  // Storage comes from get_resource_usage. get_backup_status does NOT return a
  // storageUsed field, so reading storage off it always yielded 0 — this page no
  // longer calls get_backup_status at all. get_protected_resources supplies the
  // resource list and counts; get_resource_usage supplies storage.
  const { data: usageData, isLoading: usageLoading } = useAcronisResourceUsage(acronisId, {
    enabled: !!acronisId,
  });

  // M365 backup lives behind get_m365_backup_status (usage counters + task
  // feed). Cloud-to-cloud M365 backups (e.g. Total Protect 365) create NO
  // resource rows, so for M365-only customers this is the ONLY Acronis data
  // that exists — without this call the whole page reads empty.
  const { data: m365Data, isLoading: m365Loading } = useAcronisM365Backup(acronisId, {
    enabled: !!acronisId,
  });

  // Parse and process data
  const { resources, resourceCount, successfulBackups, failedBackups, successRate, totalStorage } = useMemo(() => {
    // Parse resources response
    const parsedResources = parseMCPResponse(resourcesData);
    const resourcesList = parsedResources?.data?.resources || [];

    // Map resources to table format. The raw Acronis status is passed through
    // untouched — the table renders it. Collapsing it here previously dropped
    // 'idle' and 'no_policies_applied', which are the statuses real resources
    // actually carry, so every resource scored as neither successful nor failed
    // and the success rate was always 0%.
    const mappedResources = resourcesList.map((r) => ({
      id: r.id,
      name: r.name || 'Unknown Resource',
      type: r.type || 'unknown',
      lastBackupTime: r.lastBackup,
      lastBackupStatus: r.status || 'unknown',
      size: null, // Size is per-backup, not resource
      protectionPlan: r.policies?.join(', ') || '--',
    }));

    const count = parsedResources?.data?.summary?.total ?? mappedResources.length;

    // Acronis resource statuses: 'ok' (healthy), 'idle' (protected, awaiting the
    // next scheduled run), 'running', 'warning', 'error', 'not_protected',
    // 'no_policies_applied'. 'idle' is the steady state of a healthy resource
    // between backups and MUST count as successful — mirrors the MSR collector
    // in Odin-MCP reportCollectorService.ts.
    const successful = mappedResources.filter(
      r => r.lastBackupStatus === 'ok' || r.lastBackupStatus === 'idle'
    ).length;
    const failed = mappedResources.filter(r => r.lastBackupStatus === 'error').length;
    const warning = mappedResources.filter(r => r.lastBackupStatus === 'warning').length;

    // Rate is over resources that have actually run, so an unconfigured resource
    // ('no_policies_applied') does not read as a backup failure. Resources present
    // but no activity yet means nothing is failing — show 100%.
    const active = successful + failed + warning;
    const rate = active > 0
      ? Math.round((successful / active) * 100)
      : (mappedResources.length > 0 ? 100 : 0);

    // Storage from get_resource_usage: { usage: [{ storage_used, ... }], totals }
    const parsedUsage = parseMCPResponse(usageData);
    const storage = parsedUsage?.data?.totals?.total_storage_used ??
                    parsedUsage?.data?.usage?.[0]?.storage_used ??
                    0;

    return {
      resources: mappedResources,
      resourceCount: count,
      successfulBackups: successful,
      failedBackups: failed,
      successRate: rate,
      totalStorage: storage,
    };
  }, [resourcesData, usageData]);

  // M365 backup status (billing-grade protected counts + recent activity)
  const m365 = useMemo(() => {
    const parsed = parseMCPResponse(m365Data);
    const d = parsed?.data;
    if (!d) return null;
    // O365Discovery is housekeeping, not backup work — hide it from the table
    const services = (d.services || []).filter((s) => s.taskType !== 'O365Discovery');
    return {
      enabled: !!d.enabled,
      seats: d.protectedItems?.seats ?? 0,
      sharepointSites: d.protectedItems?.sharepointSites ?? 0,
      teams: d.protectedItems?.teams ?? 0,
      storageBytes: d.cloudToCloudStorageBytes ?? 0,
      lastBackup: d.lastBackup,
      services,
    };
  }, [m365Data]);

  // Combined loading state
  const isLoading = isMappingLoading || resourcesLoading || usageLoading;

  // Show mapping warning if tenant selected but no Acronis mapping
  const showMappingWarning = cippTenantId && !hasAcronisMapping && !isMappingLoading;

  // M365 backup activity table columns. Task runs are runs-in-a-window, NOT
  // protected-item counts — the protected counts are the Statistic tiles above.
  const m365ActivityColumns = [
    {
      title: 'Service',
      dataIndex: 'service',
      key: 'service',
    },
    {
      title: 'Items Backed Up',
      dataIndex: 'distinctItems',
      key: 'distinctItems',
    },
    {
      title: 'Task Runs',
      dataIndex: 'taskCount',
      key: 'taskCount',
    },
  ];

  // Resource table columns
  const resourceColumns = [
    {
      title: 'Resource Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const typeColors = {
          server: 'blue',
          virtual_machine: 'purple',
          m365: 'cyan',
          workstation: 'green',
        };
        return <Tag color={typeColors[type] || 'default'}>{type || 'Unknown'}</Tag>;
      },
      filters: [
        { text: 'Server', value: 'server' },
        { text: 'Virtual Machine', value: 'virtual_machine' },
        { text: 'M365', value: 'm365' },
        { text: 'Workstation', value: 'workstation' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'Last Backup',
      dataIndex: 'lastBackupTime',
      key: 'lastBackup',
      render: (date) => date ? new Date(date).toLocaleString() : 'Never',
      sorter: (a, b) => new Date(a.lastBackupTime || 0) - new Date(b.lastBackupTime || 0),
    },
    {
      title: 'Status',
      dataIndex: 'lastBackupStatus',
      key: 'status',
      render: (status) => {
        const config = {
          ok: { color: 'green', icon: <CheckCircleOutlined />, text: 'Protected' },
          success: { color: 'green', icon: <CheckCircleOutlined />, text: 'Protected' },
          protected: { color: 'green', icon: <CheckCircleOutlined />, text: 'Protected' },
          // 'idle' is the steady state of a healthy protected resource between
          // scheduled runs — it is NOT an absence of backup.
          idle: { color: 'green', icon: <CheckCircleOutlined />, text: 'Protected' },
          error: { color: 'red', icon: <CloseCircleOutlined />, text: 'Failed' },
          failed: { color: 'red', icon: <CloseCircleOutlined />, text: 'Failed' },
          running: { color: 'blue', icon: <ClockCircleOutlined />, text: 'Running' },
          warning: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Warning' },
          not_protected: { color: 'default', icon: null, text: 'Not Protected' },
          no_policies_applied: { color: 'orange', icon: null, text: 'No Backup Plan' },
        };
        const { color, icon, text } = config[status] || { color: 'default', text: status || 'Unknown' };
        return <Tag color={color} icon={icon}>{text}</Tag>;
      },
      filters: [
        { text: 'Protected', value: 'idle' },
        { text: 'No Backup Plan', value: 'no_policies_applied' },
        { text: 'Failed', value: 'error' },
        { text: 'Running', value: 'running' },
        { text: 'Warning', value: 'warning' },
      ],
      onFilter: (value, record) => record.lastBackupStatus === value,
    },
    {
      title: 'Protection Plan',
      dataIndex: 'protectionPlan',
      key: 'plan',
      ellipsis: true,
    },
  ];

  return (
    <DashboardLayout>
      <PageHeader>
        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 8 }}>
          <DatabaseOutlined style={{ marginRight: 12, color: 'var(--odin-accent)' }} />
          Acronis Cyber Protect
        </Title>
        <Text style={{ color: 'var(--odin-text-secondary)' }}>
          {selectedTenant
            ? `${selectedTenant.displayName} - Acronis Cyber Protect${customer ? ` (${customer.displayName})` : ''}`
            : 'All Tenants - Backup Status'}
        </Text>
      </PageHeader>

      {showMappingWarning && (
        <MappingAlert
          message={
            <span>
              <LinkOutlined style={{ marginRight: 8 }} />
              No Acronis mapping found for this tenant. Configure the mapping in Odin-MCP customer configuration.
            </span>
          }
          type="warning"
          showIcon={false}
        />
      )}

      {/* Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={CloudUploadOutlined}
            value={resourceCount}
            label="Protected Resources"
            loading={isLoading}
            tooltip="Total resources protected by Acronis (using get_protected_resources)"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={CheckCircleOutlined}
            value={successfulBackups}
            label="Successful Backups"
            color="var(--odin-success)"
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={CloseCircleOutlined}
            value={failedBackups}
            label="Failed Backups"
            color="var(--odin-error)"
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StorageCard>
            <Statistic
              title="Storage Used"
              value={formatBytes(totalStorage)}
              prefix={<HddOutlined style={{ color: 'var(--odin-accent)' }} />}
              loading={isLoading}
            />
          </StorageCard>
        </Col>
      </Row>

      {/* Microsoft 365 Backup */}
      {acronisId && (
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <DataCard title="Microsoft 365 Backup" icon={CloudOutlined} loading={m365Loading}>
              {m365?.enabled ? (
                <>
                  <Row gutter={[16, 16]}>
                    <Col xs={12} md={6}>
                      <Statistic
                        title="Protected Seats"
                        value={m365.seats}
                        valueStyle={{ color: 'var(--odin-success)' }}
                      />
                      <Text style={{ color: 'var(--odin-text-tertiary)', fontSize: 12 }}>
                        Mailbox + OneDrive per user
                      </Text>
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic
                        title="SharePoint Sites"
                        value={m365.sharepointSites}
                        valueStyle={{ color: 'var(--odin-text-primary)' }}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic
                        title="Teams"
                        value={m365.teams}
                        valueStyle={{ color: 'var(--odin-text-primary)' }}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic
                        title="M365 Cloud Storage"
                        value={formatBytes(m365.storageBytes)}
                        valueStyle={{ color: 'var(--odin-text-primary)' }}
                      />
                    </Col>
                  </Row>
                  <div style={{ marginTop: 16 }}>
                    <Text style={{ color: 'var(--odin-text-secondary)', marginRight: 8 }}>
                      Last successful backup:
                    </Text>
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      {m365.lastBackup ? new Date(m365.lastBackup).toLocaleString() : 'Unknown'}
                    </Tag>
                  </div>
                  {m365.services.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <Text style={{ color: 'var(--odin-text-tertiary)', fontSize: 12 }}>
                        Recent backup activity from the Acronis task window — task runs are not
                        protected-item counts
                      </Text>
                      <StyledTable
                        dataSource={m365.services}
                        columns={m365ActivityColumns}
                        rowKey="taskType"
                        pagination={false}
                        size="small"
                        style={{ marginTop: 8 }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Text style={{ color: 'var(--odin-text-tertiary)' }}>
                  No Microsoft 365 backup detected for this tenant.
                </Text>
              )}
            </DataCard>
          </Col>
        </Row>
      )}

      {/* Success Rate */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <DataCard title="Backup Success Rate" icon={CheckCircleOutlined}>
            <Row align="middle" gutter={24}>
              <Col xs={24} md={8}>
                <Progress
                  type="circle"
                  percent={successRate}
                  strokeColor={successRate >= 95 ? 'var(--odin-success)' : successRate >= 80 ? 'var(--odin-warning)' : 'var(--odin-error)'}
                  trailColor="var(--odin-border)"
                  format={(percent) => (
                    <span style={{ color: 'var(--odin-text-primary)', fontSize: 24, fontWeight: 700 }}>
                      {percent}%
                    </span>
                  )}
                />
              </Col>
              <Col xs={24} md={16}>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Statistic
                      title="Total Resources"
                      value={resourceCount}
                      valueStyle={{ color: 'var(--odin-text-primary)' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Successful"
                      value={successfulBackups}
                      valueStyle={{ color: 'var(--odin-success)' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Failed"
                      value={failedBackups}
                      valueStyle={{ color: 'var(--odin-error)' }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </DataCard>
        </Col>
      </Row>

      {/* Resources Table */}
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <DataCard
            title="Protected Resources"
            icon={DatabaseOutlined}
            loading={isLoading}
            empty={!resources?.length && !showMappingWarning}
            emptyText={selectedTenant ? 'No resources found' : 'Select a tenant to view resources'}
          >
            <StyledTable
              dataSource={resources || []}
              columns={resourceColumns}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true }}
              size="small"
            />
          </DataCard>
        </Col>
      </Row>
    </DashboardLayout>
  );
};

export default AcronisPage;
